import { useState } from 'react';
import { reportError } from '../utils/adminReport';

// Orchestrator API Base URL (từ .env.production hoặc .env.local)
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

// Helper: Convert blob to data URL (unused but kept for future use)
// eslint-disable-next-line no-unused-vars
const _blobToDataUrl = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(blob);
});

const base64ToBlob = (base64, mimeType = 'image/png') => {
  try {
    const cleaned = base64.includes(',') ? base64.split(',')[1] : base64;
    const byteCharacters = atob(cleaned);
    const byteArrays = [];

    const sliceSize = 512;
    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: mimeType });
  } catch (err) {
    console.error('base64ToBlob error:', err);
    return null;
  }
};

export const useImageProcessing = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState('');

  const processImage = async (promptTitle, imageFile, strength, serviceType = 'image2image') => {
    setLoading(true);
    setError(null);
    setResult(null);
    setProgress('Đang yêu cầu Worker...');

    try {
      if (!imageFile) {
        throw new Error('Vui lòng chọn ảnh trước khi gửi.');
      }

      // Step 1: Request worker from Orchestrator
      console.log('🔍 Step 1: Requesting worker from Orchestrator...');
      console.log(`   Service Type: ${serviceType}, Prompt: ${promptTitle}`);
      setProgress('Đang tìm Worker khả dụng...');
      
      const requestResponse = await fetch(`${API_BASE_URL}/api/task/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          service: serviceType,  // Task type từ frontend config
          prompt_title: promptTitle,  // Prompt identifier (hifu, laser, filter)
          provider: 'gemini',  // AI platform
          strength: strength
        })
      });

      if (!requestResponse.ok) {
        const errorText = await requestResponse.text();
        throw new Error(errorText || 'Không có Worker khả dụng');
      }

      const { task_id, worker_url, upload_token, prompt_detail, provider } = await requestResponse.json();
      console.log('✅ Worker assigned:', { task_id, worker_url, provider });
      
      // Safely log prompt_detail (may be undefined or empty)
      if (prompt_detail) {
        const preview = prompt_detail.length > 100 ? prompt_detail.substring(0, 100) + '...' : prompt_detail;
        console.log('📝 Prompt received:', preview);
      } else {
        console.log('⚠️  No prompt_detail in response');
      }
      
      // Log provider info
      console.log(`🤖 Provider: ${provider || 'gemini (default)'}`);
      
      // Step 2: Upload directly to Worker
      console.log('📤 Step 2: Uploading to Worker...');
      setProgress('Đang upload ảnh tới Worker...');
      
      const formData = new FormData();
      formData.append('task_id', task_id);
      formData.append('upload_token', upload_token);
      formData.append('image', imageFile);
      formData.append('service', serviceType);  // Task type từ frontend
      formData.append('prompt_title', promptTitle);  // Prompt identifier
      formData.append('provider', provider || 'gemini');  // AI platform
      formData.append('prompt', prompt_detail);  // Full prompt text
      if (strength) {
        formData.append('strength', strength.toString());
      }

      setProgress('Đang xử lý với Gemini AI...');
      
      const uploadResponse = await fetch(`${worker_url}/api/upload`, {
        method: 'POST',
        body: formData
      });

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        throw new Error(errorText || 'Worker xử lý thất bại');
      }

      const resultData = await uploadResponse.json();
      console.log('✅ Worker completed:', resultData);

      // Parse result
      let dataUrl = null;
      let blob = null;

      if (resultData.image) {
        const isDataUrl = resultData.image.startsWith('data:');
        dataUrl = isDataUrl ? resultData.image : `data:image/png;base64,${resultData.image}`;
        blob = base64ToBlob(resultData.image, 'image/png');
      }

      const parsed = {
        blob: blob || null,
        dataUrl: dataUrl || null,
        response: resultData
      };

      setResult(parsed);
      setLoading(false);
      setProgress('');
      return parsed;

    } catch (err) {
      console.error('Error processing image:', err);
      
      // Report error to admin
      reportError(
        err,
        'Frontend/ImageProcessing',
        {
          prompt_title: promptTitle,
          service: serviceType,  // Dùng serviceType thay vì hardcode
          has_image: !!imageFile,
          strength: strength
        }
      ).catch(reportErr => {
        console.warn('Failed to send error report:', reportErr);
      });
      
      setError(err.message || 'Lỗi không xác định khi xử lý ảnh.');
      setLoading(false);
      setProgress('');
      throw err;
    }
  };

  const downloadResult = async (filename) => {
    if (!result) {
      return;
    }

    try {
      let blob = result.blob;

      if (!blob && result.dataUrl) {
        blob = await fetch(result.dataUrl).then((res) => res.blob());
      }

      if (!blob) {
        throw new Error('Không tìm thấy dữ liệu ảnh để tải xuống.');
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.warn('Download failed', err);
    }
  };

  const reset = () => {
    setResult(null);
    setError(null);
    setLoading(false);
    setProgress('');
  };

  return {
    loading,
    result,
    error,
    progress,
    processImage,
    downloadResult,
    reset
  };
};
